import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ds/Tip";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Check, CreditCard, Download, Zap } from "@/components/ds/icons";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { handleApiError } from "@/lib/api-error";

interface Plan {
  id: number;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  precio_mensual: string;
  cantidad_cupos: number;
  almacenamiento_gb: number;
  caracteristicas: string[];
  orden: number;
  is_active: boolean;
}

interface Suscripcion {
  id: number;
  estado: "activa" | "cancelada" | "morosa";
  cantidad_cupos: number;
  licencias_usadas: number;
  proximo_cobro: string | null;
  plan: Plan;
}

interface Pago {
  id: number;
  numero_factura: string;
  monto: string;
  estado: "pagado" | "pendiente" | "fallido";
  fecha_pago: string;
  metodo_pago: string | null;
}

const formatCLP = (value: string | number) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(typeof value === "string" ? Number(value) : value);

const formatDate = (iso: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const estadoBadgeVariant = (estado: Pago["estado"]) =>
  estado === "pagado" ? "default" : estado === "pendiente" ? "secondary" : "destructive";

export function Suscripcion() {
  const [suscripcion, setSuscripcion] = useState<Suscripcion | null>(null);
  const [planes, setPlanes] = useState<Plan[]>([]);
  const [pagos, setPagos] = useState<Pago[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasNoSuscripcion, setHasNoSuscripcion] = useState(false);

  const cargar = useCallback(async () => {
    setIsLoading(true);
    setHasNoSuscripcion(false);
    try {
      const [suscRes, planesRes, pagosRes] = await Promise.all([
        api
          .get<{ data: Suscripcion }>("/me/suscripcion")
          .then((r) => r.data)
          .catch((err) => {
            if (err instanceof ApiError && err.status === 404) {
              setHasNoSuscripcion(true);
              return null;
            }
            throw err;
          }),
        api.get<{ data: Plan[] }>("/planes").then((r) => r.data),
        api
          .get<{ data: Pago[] }>("/me/suscripcion/pagos")
          .then((r) => r.data)
          .catch((err) => {
            if (err instanceof ApiError && err.status === 404) return [];
            throw err;
          }),
      ]);

      setSuscripcion(suscRes);
      setPlanes(planesRes);
      setPagos(pagosRes);
    } catch (err) {
      handleApiError(err, "No se pudieron cargar los datos de suscripción");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cancelar = async () => {
    try {
      await api.post("/me/suscripcion/cancelar");
      toast.success("Suscripción cancelada");
      await cargar();
    } catch (err) {
      handleApiError(err, "No se pudo cancelar la suscripción");
    }
  };

  const cambiarPlan = async (planId: number) => {
    try {
      await api.post("/me/suscripcion/cambiar-plan", { plan_id: planId });
      toast.success("Plan actualizado correctamente");
      await cargar();
    } catch (err) {
      handleApiError(err, "No se pudo cambiar el plan");
    }
  };

  const simularCobro = async () => {
    try {
      await api.post("/me/suscripcion/cobrar-ahora");
      toast.success("Cobro simulado registrado");
      await cargar();
    } catch (err) {
      handleApiError(err, "No se pudo simular el cobro");
    }
  };

  if (isLoading) {
    return <SuscripcionSkeleton />;
  }

  if (hasNoSuscripcion || !suscripcion) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Suscripción y Pagos</h1>
          <p className="text-muted-foreground">Gestión de plan y facturación</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Aún no tienes una suscripción asociada a tu cuenta.
          </CardContent>
        </Card>
      </div>
    );
  }

  const isCancelada = suscripcion.estado === "cancelada";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Suscripción y Pagos</h1>
        <p className="text-muted-foreground">Gestión de plan y facturación</p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card className="md:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Plan Actual</CardTitle>
                <CardDescription>Detalles de tu suscripción</CardDescription>
              </div>
              <Badge
                variant={isCancelada ? "destructive" : "default"}
                className="text-base px-3 py-1"
              >
                {isCancelada ? "Cancelada" : suscripcion.plan.nombre}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground">Precio Mensual</p>
                <p className="text-2xl font-bold">{formatCLP(suscripcion.plan.precio_mensual)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Próximo Pago</p>
                <p className="text-lg font-semibold">{formatDate(suscripcion.proximo_cobro)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Licencias</p>
                <p className="text-lg font-semibold">
                  {suscripcion.licencias_usadas} / {suscripcion.cantidad_cupos}
                </p>
              </div>
            </div>

            {suscripcion.plan.caracteristicas.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold">Características Incluidas</h3>
                <div className="grid gap-2 md:grid-cols-2">
                  {suscripcion.plan.caracteristicas.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isCancelada && (
              <div className="flex gap-2">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline">Cancelar Suscripción</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Cancelar la suscripción?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tu plan quedará marcado como cancelado y no se generarán nuevos cobros.
                        Esta acción no se puede deshacer desde la app.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Volver</AlertDialogCancel>
                      <AlertDialogAction onClick={cancelar}>
                        Sí, cancelar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Método de Pago</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg border border-dashed p-3 text-muted-foreground">
              <CreditCard className="h-8 w-8" />
              <div className="flex-1">
                <p className="font-medium">Sin método configurado</p>
                <p className="text-sm">Disponible próximamente</p>
              </div>
            </div>
            <Button variant="outline" className="w-full" disabled>
              Agregar Tarjeta
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Historial de Pagos</CardTitle>
              <CardDescription>Facturas y comprobantes</CardDescription>
            </div>
            {import.meta.env.DEV && !isCancelada && (
              <Button variant="outline" size="sm" onClick={simularCobro}>
                <Zap className="mr-2 h-4 w-4" />
                Simular cobro
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {pagos.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              Aún no hay pagos registrados.
            </div>
          ) : (
            <div className="space-y-3">
              {pagos.map((pago) => (
                <div
                  key={pago.id}
                  className="flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-accent"
                >
                  <div>
                    <p className="font-medium">Factura #{pago.numero_factura}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(pago.fecha_pago)}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="font-semibold">{formatCLP(pago.monto)}</p>
                    <Badge variant={estadoBadgeVariant(pago.estado)} className="capitalize">
                      {pago.estado}
                    </Badge>
                    <Tip texto="Próximamente">
                      <Button variant="ghost" size="sm" disabled aria-label="Descargar comprobante">
                        <Download className="h-4 w-4" />
                      </Button>
                    </Tip>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Planes Disponibles</CardTitle>
          <CardDescription>
            {isCancelada
              ? "Tu suscripción está cancelada — no puedes cambiar de plan."
              : "Cambia tu plan en cualquier momento."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {planes.map((plan) => {
              const esActual = plan.id === suscripcion.plan.id;
              const excedeCupos = !esActual && suscripcion.licencias_usadas > plan.cantidad_cupos;
              const disabled = esActual || isCancelada || excedeCupos;

              return (
                <Card key={plan.id} className={esActual ? "border-primary" : ""}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{plan.nombre}</CardTitle>
                      {esActual && <Badge>Actual</Badge>}
                    </div>
                    <div className="pt-2">
                      <span className="text-3xl font-bold">{formatCLP(plan.precio_mensual)}</span>
                      <span className="text-muted-foreground">/mes</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4" />
                        <span>{plan.cantidad_cupos} licencias</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4" />
                        <span>{plan.almacenamiento_gb} GB de almacenamiento</span>
                      </div>
                      {plan.caracteristicas.slice(0, 3).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Check className="h-4 w-4" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    {esActual ? (
                      <Button variant="secondary" className="w-full" disabled>
                        Plan Actual
                      </Button>
                    ) : (
                      <AlertDialog>
                        {/* `envolver`: el `disabled` vive en el Button, un nivel
                            por debajo del disparador que ve el Tip. */}
                        <Tip
                          texto={excedeCupos ? `Bajar a ${plan.cantidad_cupos} cupos liberaría licencias` : undefined}
                          envolver
                          claseEnvoltorio="w-full"
                        >
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full"
                            disabled={disabled}
                          >
                            {excedeCupos ? "Cupos insuficientes" : "Seleccionar"}
                          </Button>
                        </AlertDialogTrigger>
                        </Tip>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Cambiar a {plan.nombre}</AlertDialogTitle>
                            <AlertDialogDescription>
                              El cambio de plan toma efecto al instante. El próximo cobro respetará
                              la fecha programada ({formatDate(suscripcion.proximo_cobro)}) con el
                              precio del nuevo plan ({formatCLP(plan.precio_mensual)}/mes).
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Volver</AlertDialogCancel>
                            <AlertDialogAction onClick={() => cambiarPlan(plan.id)}>
                              Confirmar cambio
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SuscripcionSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-9 w-64" />
        <Skeleton className="mt-2 h-4 w-80" />
      </div>
      <div className="grid gap-6 md:grid-cols-3">
        <Card className="md:col-span-2">
          <CardContent className="space-y-4 pt-6">
            <Skeleton className="h-6 w-40" />
            <div className="grid gap-4 md:grid-cols-3">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </div>
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-12" />
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardContent className="space-y-3 pt-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

